import { ACCOUNT_STATUSES, ADMIN_USER_DISPLAY_STATUSES } from '@/constants';
import type { AdminUserDisplayStatus } from '@/constants';
import type { AdminUser } from '@/types/adminUsers';

/**
 * Single display status for an admin user row. Priority: blocked account →
 * pending verification account → unverified email → active.
 */
export function getUserStatus(
  user: Pick<AdminUser, 'account_status' | 'email_verified'>,
): AdminUserDisplayStatus {
  if (user.account_status === ACCOUNT_STATUSES.BLOCKED) return ADMIN_USER_DISPLAY_STATUSES.BLOCKED;
  if (user.account_status === ACCOUNT_STATUSES.PENDING_VERIFICATION) {
    return ADMIN_USER_DISPLAY_STATUSES.PENDING;
  }
  if (!user.email_verified) return ADMIN_USER_DISPLAY_STATUSES.UNVERIFIED;
  return ADMIN_USER_DISPLAY_STATUSES.ACTIVE;
}

interface FormatUserDateOptions {
  withTime?: boolean;
  shortYear?: boolean;
}

/**
 * Numeric date in the active UI locale, optionally followed by the time
 * separated by a plain space (e.g. `27.09.26 11:11`) — no locale comma.
 */
export function formatUserDate(
  value: string,
  locale: string,
  { withTime = false, shortYear = false }: FormatUserDateOptions = {},
): string {
  const datePart = new Date(value).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: shortYear ? '2-digit' : 'numeric',
  });
  return withTime ? `${datePart} ${formatUserTime(value, locale)}` : datePart;
}

/** Hours and minutes in the active UI locale. */
export function formatUserTime(value: string, locale: string): string {
  return new Date(value).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}
